import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { MultiAutoComplete } from "@/components/MultiAutoComplete/MultiAutoComplete";
import { TextArea } from "@/components/TextArea/TextArea";

import { CompanyEnrichAutoComplete } from "@/pages/Private/pages/CompanyEnrich/components/CompanyEnrichAutoComplete";
import { DEPARTMENT_OPTIONS } from "@/pages/Private/pages/CompanyEnrich/constants/department.constants";
import { EMPLOYEE_RANGES } from "@/pages/Private/pages/CompanyEnrich/constants/employees.constants";
import { SENIORITY_OPTIONS } from "@/pages/Private/pages/CompanyEnrich/constants/seniority.constants";

import { TagInput } from "../TagInput";

import { TrackFilterValues } from "./trackFilters";
import { TrackFilterLookups } from "./useTrackFilterLookups";

/**
 * The pane's four primary filters (mockup: Position / Seniority / Department /
 * Personal location). Everything rarer lives behind "Advanced filters".
 */
export const TrackFilterFields: FunctionComponent<{
	values: TrackFilterValues;
	onChange: (patch: Partial<TrackFilterValues>) => void;
	lookups: TrackFilterLookups;
}> = ({ values, onChange, lookups }) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`onboarding.track.${key}`);

	return (
		<>
			<CompanyEnrichAutoComplete
				allowCustomValues
				fetchFn={lookups.fetchPositions}
				handleSelect={(items) => onChange({ positionQuery: items ?? [] })}
				label={ts("filterPosition")}
				placeholder={ts("filterPositionPlaceholder")}
				selectedItems={values.positionQuery}
				testId="track-filter-position"
			/>
			<MultiAutoComplete
				stayOn
				data={SENIORITY_OPTIONS}
				handleSelect={(items) => onChange({ seniority: items ?? [] })}
				label={ts("filterSeniority")}
				placeholder={ts("filterSeniorityPlaceholder")}
				selectedItems={values.seniority}
				testId="track-filter-seniority"
			/>
			<MultiAutoComplete
				stayOn
				data={DEPARTMENT_OPTIONS}
				handleSelect={(items) => onChange({ department: items ?? [] })}
				label={ts("filterDepartment")}
				placeholder={ts("filterDepartmentPlaceholder")}
				selectedItems={values.department}
				testId="track-filter-department"
			/>
			<div>
				<div className="mb-2 text-sm font-medium text-gray-900">{ts("filterSkills")}</div>
				<TagInput
					placeholder={ts("filterSkillsPlaceholder")}
					values={values.skills}
					onChange={(skills) => onChange({ skills })}
				/>
			</div>
			<CompanyEnrichAutoComplete
				fetchFn={lookups.fetchCountries}
				handleSelect={(items) => onChange({ countries: items ?? [] })}
				label={ts("filterLocation")}
				placeholder={ts("filterLocationPlaceholder")}
				selectedItems={values.countries}
				testId="track-filter-location"
			/>
			<MultiAutoComplete
				stayOn
				data={lookups.industries.map((industry) => ({
					id: industry.name,
					title: industry.name,
				}))}
				handleSelect={(items) =>
					onChange({
						// Map back to the full records — the search filters on their NAICS codes.
						industries: lookups.industries.filter((industry) =>
							(items ?? []).some((item) => item.id === industry.name)
						),
					})
				}
				label={ts("filterIndustry")}
				placeholder={ts("filterIndustryPlaceholder")}
				selectedItems={values.industries.map((industry) => ({
					id: industry.name,
					title: industry.name,
				}))}
				testId="track-filter-industry"
			/>
			<MultiAutoComplete
				stayOn
				data={EMPLOYEE_RANGES}
				handleSelect={(items) => onChange({ employees: items ?? [] })}
				label={ts("filterCompanySize")}
				placeholder={ts("filterCompanySizePlaceholder")}
				selectedItems={values.employees}
				testId="track-filter-company-size"
			/>
			<CompanyEnrichAutoComplete
				fetchFn={lookups.fetchCountries}
				handleSelect={(items) => onChange({ companyCountries: items ?? [] })}
				label={ts("filterCompanyLocation")}
				placeholder={ts("filterCompanyLocationPlaceholder")}
				selectedItems={values.companyCountries}
				testId="track-filter-company-location"
			/>
			<div>
				<div className="mb-2 text-sm font-medium text-gray-900">{ts("filterKeywords")}</div>
				<TagInput
					placeholder={ts("filterKeywordsPlaceholder")}
					values={values.keywords}
					onChange={(keywords) => onChange({ keywords })}
				/>
			</div>
			<TextArea
				showError={false}
				handleChange={(event) => onChange({ semanticQuery: event.target.value })}
				label={ts("filterSemantic")}
				labelClassName="mb-2 block text-sm font-medium text-gray-900"
				name="semanticQuery"
				placeholder={ts("filterSemanticPlaceholder")}
				value={values.semanticQuery}
			/>
		</>
	);
};
